import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-base font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        default:
          'bg-button-primary-static-background text-button-primary-static-text hover:bg-button-primary-hover-background hover:text-button-primary-hover-text disabled:bg-button-primary-disabled-background disabled:text-button-primary-disabled-text',
        secondary:
          'bg-button-secondary-static-background text-button-secondary-static-text hover:bg-button-secondary-hover-background hover:text-button-secondary-hover-text disabled:bg-button-secondary-disabled-background disabled:text-button-secondary-disabled-text',
        destructive:
          'bg-button-destructive-static-background text-button-destructive-static-text hover:bg-button-destructive-hover-background hover:text-button-destructive-hover-text disabled:bg-button-destructive-disabled-background disabled:text-button-destructive-disabled-text',
        success:
          'bg-button-success-static-background text-button-success-static-text hover:bg-button-success-hover-background hover:text-button-success-hover-text disabled:bg-button-success-disabled-background disabled:text-button-success-disabled-text',
        warning:
          'bg-button-warning-static-background text-button-warning-static-text hover:bg-button-warning-hover-background hover:text-button-warning-hover-text disabled:bg-button-warning-disabled-background disabled:text-button-warning-disabled-text',
        outline:
          'bg-button-outline-static-background text-button-outline-static-text border border-button-outline-static-border hover:bg-button-outline-hover-background hover:text-button-outline-hover-text disabled:bg-button-outline-disabled-background disabled:text-button-outline-disabled-text disabled:opacity-60',
        ghost:
          'bg-button-ghost-static-background text-button-ghost-static-text hover:bg-button-ghost-hover-background hover:text-button-ghost-hover-text disabled:bg-button-ghost-disabled-background disabled:text-button-ghost-disabled-text',
        link: 'bg-button-link-static-background text-button-link-static-text hover:bg-button-link-hover-background hover:text-button-link-hover-text disabled:bg-button-link-disabled-background disabled:text-button-link-disabled-text',
        dark: 'bg-gray-900 text-white hover:bg-black hover:text-white disabled:bg-gray-50 disabled:text-gray-200',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3 text-xs',
        md: 'h-10 px-4 py-2',
        lg: 'h-11 rounded-md px-8 text-lg',
        xl: 'h-12 rounded-md px-10 text-xl',
        '2xl': 'h-13 rounded-md px-12 text-2xl',
        '3xl': 'h-14 rounded-md px-14 text-3xl',
        '4xl': 'h-15 rounded-md px-16 text-4xl',
        '5xl': 'h-16 rounded-md px-18 text-5xl',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
});
Button.displayName = 'Button';

export { Button, buttonVariants };
